'use client';

import { creatorInfoFromStoreProfile } from '@ps/functions/bio/creatorInfo';
import type { ProfileSlim } from '@ps/functions/bio/profileSlim';
import { useIsAppUser } from '@ps/functions/hooks/useIsAppUser';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models';
import { Dialog, DialogContent, DialogTitle } from '@ps/ui/atoms/dialog';
import { UserProfileProvider } from '@ps/ui/contexts/user-profile/provider';

import { SubscribeProvider } from '../contexts/subscribe/provider';
import { SubscribeInAppAlert } from './SubscribeInAppAlert';
import { SubscribeModal } from './SubscribeModal';

interface SubscribeDialogProps {
  open: boolean;
  /** Creator profile the picker renders (banner, name) — no `customization`. */
  profile: ProfileSlim;
  /** Preselect a tier (RN `defaultLevel`). */
  defaultLevel?: PopStoreExtendedSubscriptionLevel;
  /** Minimum selectable tier. */
  minLevel?: PopStoreExtendedSubscriptionLevel;
  /**
   * PDP-only: post-success redirect for the paid-tier checkout (`redirect_to`),
   * so the buyer returns to the product after subscribing. Other callers omit it.
   */
  successRedirectUrl?: string;
  /**
   * PDP-only: soft-navigate to `/checkout` (SPA) rather than a hard document
   * nav. `SubscribeModal` closes this picker (`onClose`) BEFORE pushing so it
   * doesn't linger behind the `@checkout` intercept (the top modal layer,
   * `z-[10100]`), while the opener product stays mounted underneath.
   */
  softCheckoutNav?: boolean;
  /**
   * Route-driven soft checkout: on a paid-tier confirm, soft-push `/checkout`
   * with NO `onClose`. Use when this dialog is mounted under a PATHNAME-driven
   * host (e.g. `MyAccountDialog` inside `MyAccountRouteModal`): leaving the
   * host's route closes the host — and its `!open` reset clears this picker —
   * so a single pathname mechanism owns all closing. Distinct from
   * {@link softCheckoutNav} (state-modal path that closes itself via `onClose`).
   */
  routeCheckoutNav?: boolean;
  /** Called with `false` when the dialog should close (backdrop/Esc/complete). */
  onOpenChange: (open: boolean) => void;
  /**
   * Fires after a successful FREE-tier follow (the picker's inline "Follow"),
   * which — unlike a paid subscribe — does NOT navigate away. Callers that gate
   * content on follow standing (e.g. the clip paywall) use it to close the
   * dialog and refresh the viewer's standing so the gate lifts without a reload.
   */
  onFollowed?: () => void;
}

/**
 * The subscribe picker as an IN-PLACE, state-controlled dialog — the shared
 * view for every NON-bio context (my-account resubscribe/change, dashboard
 * my-subscriptions). No navigation, no URL change: the caller owns `open`.
 *
 * The bio context keeps the ROUTE approach (`/<url_code>/subscribe` +
 * `@subscribe` intercept) for a shareable, deep-linkable URL — the shared
 * `SubscribeRouteContent` composes the same {@link SubscribeProvider} +
 * {@link SubscribeModal} behind that route (in a dialog for the intercept, on
 * the bio backdrop for the standalone page). Both paths render the identical
 * underlying view; only the mount (route vs local modal) differs.
 *
 * Self-contained providers: `SubscribeModal` reads the private profile
 * (`useCurrentUserPrivateProfile`) for the billing-country consent gate, so this
 * mounts its own `UserProfileProvider` — the dashboard tree carries `AuthProvider`
 * but not the profile provider, and nesting is a harmless no-op where it already
 * exists (e.g. under the route layout).
 */
export function SubscribeDialog({
  open,
  onOpenChange,
  profile,
  defaultLevel,
  minLevel,
  successRedirectUrl,
  softCheckoutNav,
  routeCheckoutNav,
  onFollowed,
}: SubscribeDialogProps) {
  // Inside the CommentSold native app subscribe payments must not run —
  // App Store IAP compliance. Legacy replaced the whole
  // `AccessRestrictionContent` with an "Alert! Please subscribe on the
  // web app" card via `AccessRestrictionAppContent`. Gating the dialog
  // itself catches every visitor entry point (clip access gate, PDP
  // subscribe gate, follow guard, wall paywall) without duplicating
  // the check at each call site.
  const isAppUser = useIsAppUser();
  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        overlayClassName="z-[10000]"
        className="border-line-strong bg-surface-1 z-[10000] flex max-h-[86vh] max-w-[400px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[420px]"
      >
        <DialogTitle className="sr-only">
          Subscribe to {profile.storeFrontName}
        </DialogTitle>
        {isAppUser ? (
          <SubscribeInAppAlert onClose={close} />
        ) : (
          <UserProfileProvider>
            <SubscribeProvider
              creatorInfo={creatorInfoFromStoreProfile(
                profile,
                null /** todo: pass store doc if available later */,
              )}
              onClose={close}
              onFollowed={onFollowed}
              successRedirectUrl={successRedirectUrl}
              softCheckoutNav={softCheckoutNav}
              routeCheckoutNav={routeCheckoutNav}
            >
              <SubscribeModal defaultLevel={defaultLevel} minLevel={minLevel} />
            </SubscribeProvider>
          </UserProfileProvider>
        )}
      </DialogContent>
    </Dialog>
  );
}
